import { NgTemplateOutlet } from '@angular/common';
import { booleanAttribute, ChangeDetectionStrategy, Component, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import type { ClassValue } from 'clsx';
import { ButtonDirective, ButtonVariants } from './button.directive';
import { DialogComponent } from './dialog/dialog.component';
import { DialogCloseDirective } from './dialog/dialog-close.directive';
import { DialogOpenDirective } from './dialog/dialog-open.directive';
import { TooltipDirective, TooltipPosition } from './tooltip/tooltip.directive';

/**
 * The single, modular button for BOTH apps. Renders a `<button>`, a `<a routerLink>`, or a
 * `<a href>` depending on inputs — so it covers navigation "buttons" too — and drives the styling
 * through the SAME `[libBtn]` directive / `buttonVariants` CVA (one source of truth: `<wh-button>`
 * and `[libBtn]` are pixel-identical). `:host { display: contents }` so the inner element is the
 * real layout box (works transparently inside flex/grid).
 *
 *   <wh-button variant="electricPurple" routerLink="/sites/new"><svg…/> New site</wh-button>
 *   <wh-button variant="outline" [loading]="saving()" (click)="save()">Save</wh-button>
 */
@Component({
  selector: 'wh-button',
  standalone: true,
  imports: [ButtonDirective, RouterLink, NgTemplateOutlet, DialogOpenDirective, DialogCloseDirective, TooltipDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      display: contents;
    }
  `,
  template: `
    @if (routerLink()) {
      <a
        libBtn
        [variant]="variant()"
        [size]="size()"
        [userClass]="mergedClass()"
        [routerLink]="routerLink()"
        [attr.title]="title()">
        <ng-container [ngTemplateOutlet]="content" />
      </a>
    } @else if (href()) {
      <a
        libBtn
        [variant]="variant()"
        [size]="size()"
        [userClass]="mergedClass()"
        [href]="href()"
        [attr.title]="title()">
        <ng-container [ngTemplateOutlet]="content" />
      </a>
    } @else {
      <!-- The tooltip and dialog directives are applied HERE, not on the host: their
           selectors demand a real button element, and libTooltip measures a box that a
           display-contents host does not have. Forwarding them keeps wh-button usable
           everywhere a bare button libBtn used to be required. -->
      <button
        libBtn
        [variant]="variant()"
        [size]="size()"
        [userClass]="mergedClass()"
        [type]="type()"
        [attr.title]="title()"
        [libTooltip]="tooltip() ?? ''"
        [libTooltipPosition]="tooltipPosition()"
        [libDialogOpenFor]="dialogOpenFor()"
        [libDialogCloseFor]="dialogCloseFor()"
        [disabled]="disabled() || loading()">
        <ng-container [ngTemplateOutlet]="content" />
      </button>
    }

    <ng-template #content>
      @if (loading()) {
        <svg class="size-3.5 shrink-0 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
      }
      <ng-content />
    </ng-template>
  `,
})
export class WhButtonComponent {
  readonly variant = input<ButtonVariants['variant']>('default');
  readonly size = input<ButtonVariants['size']>('default');
  readonly type = input<'button' | 'submit' | 'reset'>('button');
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly loading = input(false, { transform: booleanAttribute });
  /** Full-width (`w-full`). */
  readonly block = input(false, { transform: booleanAttribute });
  /** Internal routerLink target — renders an `<a routerLink>` instead of a `<button>`. */
  readonly routerLink = input<string | unknown[] | null>(null);
  /** External href — renders an `<a href>`. */
  readonly href = input<string | null>(null);
  /** Native tooltip forwarded to the rendered `<button>`/`<a>` (the host is `display:contents`). */
  readonly title = input<string | null>(null);
  /** Rich tooltip (`libTooltip`). Empty string = no tooltip. */
  readonly tooltip = input<string | null>(null);
  readonly tooltipPosition = input<TooltipPosition>('top');
  /** Opens the given dialog on click — same contract as `[libDialogOpenFor]`. */
  readonly dialogOpenFor = input<DialogComponent | null>(null);
  /** Closes the given dialog on click — same contract as `[libDialogCloseFor]`. */
  readonly dialogCloseFor = input<DialogComponent | null>(null);
  /** Extra classes merged onto the button (tailwind-merge dedupes vs the variant classes). */
  readonly userClass = input<ClassValue>('');

  protected mergedClass(): ClassValue {
    return [this.block() ? 'w-full' : '', this.userClass()];
  }
}
